import { Controller } from "@hotwired/stimulus"

// Phase 3.5 Tier 1 — ACC project picker on the per-project linker form.
// Lazy-loads /connections/:id/acc_projects.json into the dropdown on
// connect, pre-selects a saved project_id if any, and writes the picked
// project's display name into the hidden `acc_project_name` field so
// the controller can persist a stable label without a second round-trip.
export default class extends Controller {
  static targets = ["select", "status", "nameField"]
  static values = {
    projectsUrl: String,
    savedId:     String
  }

  connect() {
    if (!this.hasSelectTarget || !this.projectsUrlValue) return
    this._fetchProjects()
  }

  projectChanged(event) {
    const opt = event.currentTarget.selectedOptions?.[0]
    if (this.hasNameFieldTarget) {
      this.nameFieldTarget.value = opt?.dataset?.name ?? opt?.textContent?.trim() ?? ""
    }
  }

  async _fetchProjects() {
    this._showStatus("Loading projects from Autodesk…")
    try {
      const response = await fetch(this.projectsUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      const json = await response.json().catch(() => ({}))

      if (!response.ok || !json.ok) {
        const message = json?.message || `Couldn't load ACC projects (HTTP ${response.status}).`
        this._showStatus(message, true)
        return
      }

      this._populate(Array.isArray(json.projects) ? json.projects : [])
    } catch (err) {
      this._showStatus(`Couldn't reach Autodesk: ${err?.message || err}`, true)
    }
  }

  _populate(projects) {
    const select = this.selectTarget
    const saved = this.savedIdValue?.toString() ?? ""
    const savedName = this._capturedSavedName(select)

    if (projects.length === 0) {
      // Preserve the previously-saved option so an edit-mode form
      // doesn't silently submit an empty acc_project_id when ACC
      // returns no projects (rare, but: revoked permission + edit).
      if (saved) {
        select.innerHTML = `<option value="${this._escape(saved)}" data-name="${this._escape(savedName)}" selected>${this._escape(savedName || saved)} (currently linked)</option>`
        this._showStatus("This hub has no projects visible — keeping the existing link.", true)
      } else {
        select.innerHTML = `<option value="">— no ACC projects in this hub —</option>`
        this._showStatus("This hub has no projects yet — create one in ACC first, then refresh.", true)
      }
      return
    }

    const matched = saved ? projects.find(p => String(p.id) === saved) : null
    const opts = ['<option value="">— pick an ACC project —</option>']

    // If the saved id isn't in the live list, prepend it as a "currently
    // linked" option so the form doesn't silently lose its value on
    // submit. The user can re-pick from the live list to overwrite.
    if (saved && !matched) {
      opts.push(
        `<option value="${this._escape(saved)}" data-name="${this._escape(savedName)}" selected>${this._escape(savedName || saved)} (currently linked — no longer in this hub)</option>`
      )
    }

    projects.forEach(p => {
      const id = String(p.id ?? "")
      const name = String(p.name ?? id)
      const selected = saved && saved === id ? " selected" : ""
      opts.push(
        `<option value="${this._escape(id)}" data-name="${this._escape(name)}"${selected}>${this._escape(name)}</option>`
      )
    })
    select.innerHTML = opts.join("")

    if (matched && this.hasNameFieldTarget) {
      this.nameFieldTarget.value = matched.name ?? ""
    }
    this._hideStatus()
  }

  // Pull the saved option's display name off the original markup
  // before we overwrite it. Used to render the "(currently linked —
  // no longer in this hub)" fallback option without losing context.
  _capturedSavedName(select) {
    if (this._cachedSavedName != null) return this._cachedSavedName
    const saved = this.savedIdValue?.toString() ?? ""
    if (!saved) {
      this._cachedSavedName = ""
      return ""
    }
    const opt = Array.from(select.options).find(o => o.value === saved)
    this._cachedSavedName = (opt?.dataset?.name ?? opt?.textContent ?? "").trim()
    return this._cachedSavedName
  }

  _showStatus(message, error = false) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden")
    this.statusTarget.classList.toggle("text-rose-700", error)
    this.statusTarget.classList.toggle("text-gray-500", !error)
  }

  _hideStatus() {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = ""
    this.statusTarget.classList.add("hidden")
  }

  _escape(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
